import { useEffect } from 'react';

const SITE_NAME = 'NICEWINE';
const DEFAULT_IMAGE = '/images/categories/rare-collections.jpg';

const clamp = (s, n) => {
  if (!s) return '';
  const clean = String(s).replace(/\s+/g, ' ').trim();
  return clean.length <= n ? clean : `${clean.slice(0, n - 1).trimEnd()}\u2026`;
};

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
  el.setAttribute('href', href);
}

/**
 * Lightweight, dependency-free SEO head manager.
 * Titles are clamped to 60 characters and descriptions to 160 characters.
 */
export function Seo({ title, description, path, image, type = 'website', jsonLd, noindex = false, lang }) {
  useEffect(() => {
    const origin = window.location.origin;
    const fullTitle = title ? clamp(title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`, 60) : SITE_NAME;
    const desc = clamp(description, 160);
    const url = `${origin}${path ?? window.location.pathname}`;
    const img = image ? (image.startsWith('http') ? image : `${origin}${image}`) : `${origin}${DEFAULT_IMAGE}`;

    document.title = fullTitle;
    if (lang) document.documentElement.lang = lang;
    upsertMeta('name', 'description', desc);
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', img);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', desc);
    upsertMeta('name', 'twitter:image', img);
    upsertLink('canonical', url);

    const scriptId = 'nw-jsonld';
    document.getElementById(scriptId)?.remove();
    if (jsonLd) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = scriptId;
      s.text = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
    return () => { document.getElementById(scriptId)?.remove(); };
  }, [title, description, path, image, type, noindex, lang, JSON.stringify(jsonLd)]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export const organizationJsonLd = (origin) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: origin,
  logo: `${origin}/favicon.svg`,
  email: 'contact@nicewine.com',
  address: { '@type': 'PostalAddress', streetAddress: '15 rue Claude Terrasse', postalCode: '75016', addressLocality: 'Paris', addressCountry: 'FR' },
});

export const productJsonLd = (p, origin, lang) => {
  const availability = p.purchasable
    ? (p.availability === 'low_stock' ? 'https://schema.org/LimitedAvailability' : 'https://schema.org/InStock')
    : (p.availability === 'out_of_stock' ? 'https://schema.org/OutOfStock' : 'https://schema.org/PreOrder');
  const images = (p.images || []).map((i) => (i.startsWith('http') ? i : `${origin}${i}`));
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: p.name,
    image: images,
    description: lang === 'fr' ? p.description_fr : p.description_en,
    sku: p.reference_id,
    brand: { '@type': 'Brand', name: p.estate || SITE_NAME },
    category: `${p.category === 'spirit' ? 'Spirits' : 'Wine'} > ${p.subcategory}`,
    additionalProperty: [
      p.vintage ? { '@type': 'PropertyValue', name: 'Vintage', value: p.vintage } : null,
      { '@type': 'PropertyValue', name: 'Format', value: p.format },
      { '@type': 'PropertyValue', name: 'Region', value: p.region },
    ].filter(Boolean),
    offers: {
      '@type': 'Offer',
      url: `${origin}/product/${p.slug || p.id}`,
      priceCurrency: 'EUR',
      price: p.price_eur,
      availability,
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: SITE_NAME },
    },
  };
};

export const breadcrumbJsonLd = (items, origin) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({ '@type': 'ListItem', position: i + 1, name: it.name, item: `${origin}${it.path}` })),
});
